import React, { useState } from 'react';

import { Bolt, CheckCircleOutline, ErrorOutline, FileUploadOutlined, QueryBuilder } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useProviderStatesSelectionMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import {
  AsyncButton, collaborativeAgreementRejectionReasonText, LightChip, Tooltip, UploadCollaborativeAgreementModal,
  ViewCollaborativeAgreementDocumentModal
} from '@uplift/ui-v2';

import { PracticeStateRow, PracticeStateStatus } from './PracticeStatesTable.types';

export type StateStatusCellProps = {
  value?: PracticeStateStatus;
  row: PracticeStateRow;
};

export const StateStatusCell: React.FC<StateStatusCellProps> = ({ value, row }) => {
  if (value === 'expired') {
    return <ExpiredChip expiration={row.licenseExpiration} />;
  }

  if (value === 'live')
    return (
      <LightChip
        color="success"
        label="Live"
        icon={<CheckCircleOutline fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'inReview')
    return (
      <LightChip
        color="default"
        label="In Review"
        icon={<QueryBuilder fontSize="small" />}
        size="medium"
      />
    );

  if (value === 'rejected') {
    return <RejectedActions row={row} />;
  }

  if (value === 'getStarted') {
    return <GetStartedButton licenseId={row.id} stateId={row.stateId} />;
  }

  if (value === 'goLive') {
    return <GoLiveButton stateId={row.stateId} />;
  }

  return null;
};

type ExpiredChipProps = {
  expiration: string;
};

const ExpiredChip: React.FC<ExpiredChipProps> = ({ expiration }) => {
  const formatted = CalendarDateService.date(expiration).format(DateFormats['MMM D, YYYY']);

  return (
    <Tooltip title={`License expired on ${formatted}. Update the license expiration date to enable actions.`}>
      <span>
        <LightChip
          color="error"
          label="Expired"
          icon={<ErrorOutline fontSize="small" />}
          size="medium"
        />
      </span>
    </Tooltip>
  );
};

type RejectedActionsProps = {
  row: PracticeStateRow;
};

const RejectedActions: React.FC<RejectedActionsProps> = ({ row }) => {
  const [ modal, setModal ] = useState<'view' | 'upload'>();
  const me = useCurrentuser();
  const agreement = row.activeCollaborativeAgreement;

  if (!agreement) return null;

  return (
    <>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title={collaborativeAgreementRejectionReasonText(agreement.rejectionReason, agreement.rejectionDetails)}>
          <span>
            <LightChip
              color="error"
              label="Rejected"
              icon={<ErrorOutline fontSize="small" />}
              size="medium"
            />
          </span>
        </Tooltip>
        <Button variant="contained" color="secondary" size="small" startIcon={<FileUploadOutlined />} onClick={() => setModal('view')}>
          Resubmit
        </Button>
      </Box>
      {modal === 'view' && (
        <ViewCollaborativeAgreementDocumentModal
          agreement={agreement}
          providerId={me.id}
          stateName={row.stateName}
          onUploadNew={() => setModal('upload')}
          onClose={() => setModal(undefined)}
        />
      )}
      {modal === 'upload' && (
        <UploadCollaborativeAgreementModal
          onClose={() => setModal(undefined)}
          license={{
            id: row.id,
            stateId: row.stateId,
            userId: me.id
          }}
        />
      )}
    </>
  );
};

type GetStartedButtonProps = {
  licenseId: string;
  stateId: string;
};

const GetStartedButton: React.FC<GetStartedButtonProps> = ({ licenseId, stateId }) => {
  const [ uploadModalOpen, setUploadModalOpen ] = useState(false);
  const me = useCurrentuser();

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        startIcon={<Bolt />}
        onClick={() => setUploadModalOpen(true)}
      >
        Get Started
      </Button>
      {uploadModalOpen && (
        <UploadCollaborativeAgreementModal
          onClose={() => setUploadModalOpen(false)}
          license={{
            id: licenseId,
            stateId: stateId,
            userId: me.id
          }}
        />
      )}
    </>
  );
};

type GoLiveButtonProps = {
  stateId: string;
};

const GoLiveButton: React.FC<GoLiveButtonProps> = ({ stateId }) => {
  const me = useCurrentuser();
  const providerStatesSelectionMutation = useProviderStatesSelectionMutation(me?.id);

  const handleGoLive = async () => {
    const stateIds = [ stateId ];

    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: false });
    await providerStatesSelectionMutation.mutateAsync({ stateIds: stateIds, setLive: true });
  };


  return (
    <AsyncButton
      variant="contained"
      color="secondary"
      size="small"
      startIcon={<CheckCircleOutline />}
      onClick={handleGoLive}
    >
      Go Live
    </AsyncButton>
  );
};
